import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ImportPreviewDialog } from "@/components/ImportPreviewDialog";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const postImport = (file, params = "", exclude = []) => {
    const form = new FormData();
    form.append("file", file);
    form.append("exclude", exclude.join(","));
    return api.post(`/vinyls/import${params}`, form);
};

export const ImportCsvButton = ({ onImported }) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setBusy(true);
        try {
            const { data } = await postImport(f, "?dry_run=true");
            setFile(f);
            setPreview(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not read file");
        } finally {
            setBusy(false);
        }
    };

    const confirm = async (excluded) => {
        setBusy(true);
        try {
            const { data } = await postImport(file, "", excluded);
            setPreview(null);
            setFile(null);
            setResult(data);
            if (data.added > 0) onImported?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <input ref={inputRef} type="file" accept=".csv,text/csv,.json,application/json" className="hidden"
                onChange={handleFile} data-testid="import-csv-input" />
            <Button variant="outline" className="gap-2 rounded-full" disabled={busy}
                onClick={() => inputRef.current?.click()} data-testid="import-csv-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="hidden sm:inline">Import</span>
            </Button>
            <ImportPreviewDialog preview={preview} busy={busy} fileName={file?.name}
                onCancel={() => { setPreview(null); setFile(null); }} onConfirm={confirm} />
            <ImportSummaryDialog result={result} onClose={() => setResult(null)} />
        </>
    );
};

const ImportSummaryDialog = ({ result, onClose }) => (
    <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg" data-testid="import-summary-dialog">
            <DialogHeader>
                <DialogTitle>Import complete</DialogTitle>
                <DialogDescription>Here's what happened with your file.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-4">
                    <p className="overline text-muted-foreground">Added</p>
                    <p className="font-display text-3xl font-bold" data-testid="import-added-count">{result?.added ?? 0}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                    <p className="overline text-muted-foreground">Skipped</p>
                    <p className="font-display text-3xl font-bold" data-testid="import-skipped-count">{result?.skipped ?? 0}</p>
                </div>
            </div>
            {result?.skipped_rows?.length > 0 && (
                <div className="max-h-56 overflow-y-auto rounded-lg border border-border divide-y divide-border text-sm"
                    data-testid="import-skipped-list">
                    {result.skipped_rows.map((s) => (
                        <div key={s.row} className="px-3 py-2" data-testid={`import-skipped-row-${s.row}`}>
                            <p className="truncate">
                                <span className="text-muted-foreground">Row {s.row}: </span>
                                {s.artist || "—"} – {s.album_title || "—"}
                            </p>
                            <p className="text-xs text-muted-foreground">{s.reason}</p>
                        </div>
                    ))}
                </div>
            )}
            <DialogFooter>
                <Button onClick={onClose} className="rounded-full" data-testid="import-summary-close-btn">Done</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);
